import { useCallback, useEffect, useRef, useState } from 'react'
import { Copy, ExternalLink, Link2, Link2Off, CheckCircle2, Printer, ShieldCheck, Trash2, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { reportAPI } from '@/lib/api/reports'
import type { ReportView, ValidationIssue } from '@/lib/types/report'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'
import { LIVE_STATUSES } from '@/lib/report/liveStatus'
import { cn } from '@/lib/utils'

interface ReportInspectorProps {
  reportId: string | null
  events?: ToolCallEvent[]
  onDeleted: () => void
  onChanged: () => void
}

/** Right pane of Reports mode: the report-as-entity card + actions.
 *  Share state is local: unknown until Share is pressed (share is idempotent
 *  create-or-return); Revoke clears it. */
export function ReportInspector({ reportId, events, onDeleted, onChanged }: ReportInspectorProps) {
  const [view, setView] = useState<ReportView | null>(null)
  const [shareToken, setShareToken] = useState<string | null>(null)
  const [issues, setIssues] = useState<ValidationIssue[] | null>(null)
  const [issueCount, setIssueCount] = useState<number | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const seenRef = useRef(new Set<string>())

  const refresh = useCallback(() => {
    if (!reportId) return
    reportAPI.getReport(reportId).then((r) => setView(r.data))
  }, [reportId])

  useEffect(() => {
    setView(null)
    setShareToken(null)
    setIssues(null)
    setIssueCount(null)
    seenRef.current.clear()
    refresh()
  }, [refresh])

  // Keep status/version current while the agent writes. Scan every event with a
  // seen-set — the socket array prepends and replaces in place, so events[0]
  // alone misses completions (the v5-behind-v3 lag from the 2026-07-07 live pass).
  useEffect(() => {
    if (!events || !reportId) return
    let fresh = false
    for (const e of events) {
      const key = `${e.call_id}:${e.data.status}`
      if (seenRef.current.has(key)) continue
      seenRef.current.add(key)
      if (
        e.type === 'tool_call' &&
        e.data.status === 'completed' &&
        e.data.params?.report_id === reportId
      ) {
        fresh = true
      }
    }
    if (fresh) refresh()
  }, [events, reportId, refresh])

  if (!reportId) {
    return <div className="p-4 text-sm text-muted-foreground">No report open.</div>
  }
  if (!view) {
    return <div className="p-4 text-sm text-muted-foreground">Loading…</div>
  }

  const isLive = LIVE_STATUSES.has(view.status)
  const written = view.outline.filter((o) => o.written).length

  const share = async () => {
    let token: string
    try {
      token = (await reportAPI.shareReport(reportId)).data.token
    } catch {
      toast.error('Could not create the share link')
      return
    }
    setShareToken(token)
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/r/${token}`)
      toast.success('Public link copied to clipboard')
    } catch {
      toast.info('Link created — copying failed, use Copy public link')
    }
  }

  const revoke = async () => {
    try {
      await reportAPI.revokeShare(reportId)
      setShareToken(null)
      toast.success('Public link revoked')
      onChanged()
    } catch {
      toast.error('Could not revoke the link')
    }
  }

  const finalize = async () => {
    try {
      const res = await reportAPI.finalizeReport(reportId)
      setView({ ...view, status: res.data.status, version: res.data.version })
      toast.success('Report finalized')
      onChanged()
    } catch {
      toast.error('Could not finalize the report')
    }
  }

  const runValidation = async () => {
    try {
      const res = await reportAPI.validateReport(reportId)
      setIssueCount(res.data.issue_count)
      const issuesRes = await reportAPI.getReportIssues(reportId)
      setIssues(issuesRes.data.actionable)
    } catch {
      toast.error('Validation failed')
    }
  }

  const doDelete = async () => {
    try {
      await reportAPI.deleteReport(reportId)
      toast.success('Report deleted')
      onDeleted()
    } catch {
      toast.error('Could not delete the report')
    }
  }

  const saveRename = async () => {
    const title = draftTitle.trim()
    setRenaming(false)
    if (!title || title === view.title) return
    try {
      await reportAPI.renameReport(reportId, title)
      setView({ ...view, title })
      onChanged()
    } catch {
      toast.error('Could not rename the report')
    }
  }

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div>
        <div>
          {renaming ? (
            <Input
              autoFocus
              value={draftTitle}
              maxLength={200}
              className="h-7 text-sm"
              onChange={(e) => setDraftTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveRename()
                if (e.key === 'Escape') setRenaming(false)
              }}
              onBlur={() => void saveRename()}
            />
          ) : (
            <div className="flex items-center gap-1">
              <h2 className="truncate text-sm font-semibold text-foreground">{view.title}</h2>
              <button
                type="button"
                aria-label="Rename report"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setDraftTitle(view.title)
                  setRenaming(true)
                }}
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        <p className="mt-1 truncate text-xs text-muted-foreground">
          {[
            view.projects.length
              ? view.projects.map((p) => p.name).join(', ')
              : null,
            `created ${new Date(view.created_at).toLocaleDateString()}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className={cn(
            'rounded-full border px-2 py-0.5',
            isLive ? 'border-[var(--report-accent)]/50 text-[var(--report-accent)]' : 'border-border',
          )}>
            {view.status}
          </span>
          <span>v{view.version}</span>
          <span>{view.profile_id}</span>
          <span>{written} / {view.outline.length} sections</span>
        </div>
      </div>
      <Separator />
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Actions</p>
        {shareToken == null ? (
          <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={share}>
            <Link2 className="h-3.5 w-3.5" /> Share link
          </Button>
        ) : (
          <>
            <Button
              variant="outline" size="sm" className="w-full justify-start gap-2"
              onClick={() => {
                void navigator.clipboard.writeText(`${window.location.origin}/r/${shareToken}`)
                toast.success('Public link copied to clipboard')
              }}
            >
              <Copy className="h-3.5 w-3.5" /> Copy public link
            </Button>
            <Button asChild variant="outline" size="sm" className="w-full justify-start gap-2">
              <a href={`/r/${shareToken}`} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" /> Open public page
              </a>
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={revoke}>
              <Link2Off className="h-3.5 w-3.5" /> Revoke link
            </Button>
          </>
        )}
        {view.status === 'complete' ? (
          <p className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--report-accent)]" /> Completed — a new
            section write reopens it.
          </p>
        ) : (
          <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={finalize}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Finalize
          </Button>
        )}
        <Button
          variant="outline" size="sm" className="w-full justify-start gap-2"
          onClick={() => window.print()}
        >
          <Printer className="h-3.5 w-3.5" /> Export PDF
        </Button>
      </div>
      <Separator />
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Validation</p>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={runValidation}>
          <ShieldCheck className="h-3.5 w-3.5" /> Run validation
        </Button>
        {issueCount != null && (
          <p className="px-1 text-xs text-muted-foreground">
            {issueCount === 0 ? 'No issues.' : `${issueCount} issue${issueCount === 1 ? '' : 's'} found.`}
          </p>
        )}
        {issues && issues.length > 0 && (
          <ul className="space-y-1">
            {issues.map((i, idx) => (
              <li key={idx} className="space-y-1 rounded-md border border-border px-2 py-1.5 text-xs">
                <div className="flex items-center gap-2">
                  {i.kind && (
                    <span className="rounded-full border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {i.kind}
                    </span>
                  )}
                  {i.section_id && <span className="font-medium text-foreground">{i.section_id}</span>}
                </div>
                <p className="text-muted-foreground">{i.hint ?? i.message ?? 'See report for details.'}</p>
                {i.fragment && (
                  <code className="block truncate rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[10px]">
                    {i.fragment}
                  </code>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Separator />
      <div className="mt-auto space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-destructive">Danger zone</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-start gap-2 border-destructive/40 text-destructive hover:bg-destructive/10">
              <Trash2 className="h-3.5 w-3.5" /> Delete report
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{view.title}"?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the report, all {view.version} versions, and any public
                share link. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
